import React, { useId } from "react";
import { motion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

const PAGE_SIZE_OPTIONS = [20, 25, 50, 100, 200];

// Page buttons: always 7 slots once there are more than 7 pages, so the bar
// keeps its width and buttons stay under the mouse while paging:
//   1 2 3 4 5 … 40  |  1 … 19 20 21 … 40  |  1 … 36 37 38 39 40
export const pageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const run = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
  if (current <= 4) return [...run(1, 5), "end-gap", total];
  if (current >= total - 3) return [1, "start-gap", ...run(total - 4, total)];
  return [1, "start-gap", current - 1, current, current + 1, "end-gap", total];
};

const pill =
  "relative inline-flex h-7 min-w-[2rem] items-center justify-center rounded-full px-1 text-[13px] font-medium tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-35";

const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  loading = false,
  className = "",
}) => {
  // One sliding highlight per pagination bar, even with several on a page.
  const highlightId = `pagination-active-${useId()}`;

  if (typeof totalCount === "number" && totalCount <= 0) {
    return null;
  }

  const hasCount = typeof totalCount === "number";
  const isShowAll = hasCount && pageSize >= totalCount;
  const pages = isShowAll ? 1 : Math.max(totalPages || 1, 1);
  const page = isShowAll ? 1 : Math.min(Math.max(currentPage || 1, 1), pages);
  const first = (page - 1) * pageSize + 1;
  const last = hasCount ? Math.min(page * pageSize, totalCount) : page * pageSize;

  const goTo = (target) => {
    if (target !== page && target >= 1 && target <= pages && !loading) onPageChange(target, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const value = e.target.value;
    onPageChange(1, value === "all" ? totalCount : Number(value));
  };

  return (
    <nav
      aria-label="Pagination"
      className={`flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row dark:border-gray-700/60 ${className}`}
    >
      <p className="text-[13px] text-gray-500 tabular-nums dark:text-gray-400">
        {hasCount ? (
          <>
            <span className="font-medium text-gray-900 dark:text-white">
              {first.toLocaleString()}–{last.toLocaleString()}
            </span>{" "}
            of {totalCount.toLocaleString()}
          </>
        ) : (
          <>Page {page} of {pages}</>
        )}
      </p>

      <div className={`flex items-center gap-3 transition-opacity ${loading ? "opacity-60" : ""}`}>
        <label className="flex items-center gap-2 text-[13px] text-gray-500 dark:text-gray-400">
          <span className="whitespace-nowrap">Rows</span>
          <span className="relative">
            <select
              value={PAGE_SIZE_OPTIONS.includes(pageSize) || !isShowAll ? pageSize : "all"}
              onChange={handlePageSizeChange}
              disabled={loading}
              aria-label="Rows per page"
              className="h-8 cursor-pointer appearance-none rounded-full border border-gray-200 bg-white py-0 pl-3 pr-7 text-[13px] font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 focus:outline-none disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
              {hasCount && <option value="all">All</option>}
            </select>
            <ChevronUpDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </span>
        </label>

        {pages > 1 && (
          <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-white p-0.5 shadow-sm dark:border-gray-600 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => goTo(page - 1)}
              disabled={page === 1 || loading}
              aria-label="Previous page"
              className={`${pill} text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white`}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            <span className="min-w-[4.5rem] px-2 text-center text-[13px] text-gray-600 tabular-nums sm:hidden dark:text-gray-300">
              {page} / {pages}
            </span>
            <div className="hidden items-center gap-0.5 sm:flex">
              {pageItems(page, pages).map((item) =>
                typeof item === "number" ? (
                  <button
                    key={item}
                    type="button"
                    onClick={() => goTo(item)}
                    disabled={loading}
                    aria-label={`Page ${item}`}
                    aria-current={item === page ? "page" : undefined}
                    className={`${pill} ${
                      item === page
                        ? "text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                    }`}
                  >
                    {item === page && (
                      <motion.span
                        layoutId={highlightId}
                        className="absolute inset-0 rounded-full bg-brand shadow-sm"
                        transition={{ type: "spring", stiffness: 500, damping: 38 }}
                      />
                    )}
                    <span className="relative">{item}</span>
                  </button>
                ) : (
                  <span key={item} className="inline-flex h-7 min-w-[2rem] items-center justify-center text-[13px] text-gray-400">
                    …
                  </span>
                ),
              )}
            </div>

            <button
              type="button"
              onClick={() => goTo(page + 1)}
              disabled={page === pages || loading}
              aria-label="Next page"
              className={`${pill} text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white`}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Pagination;
